import debouncePromise from 'debounce-promise';
import _ from 'lodash';
import { FusionApiService, FusionSearchResultStore, getStoreApiService, MoUtils, SearchDSLQuery, SearchDSLRequest } from 'ui-common';

export class FusionSearchDSLResultStore extends FusionSearchResultStore {
  static defaultConfig = {
    loadDebounceDelay: 100,

    api: {
      service: 'searchDSL',

      search: {
        name: 'query',
        supportResultGrouping: true,

        transform: (response, _options, storeContext: FusionSearchDSLResultStore) => {
          const result = storeContext.transFormSearchResult(response);

          return {
            ...result,
            fusionMeta: response.debugResponse,
          };
        },
      },
    },
  };

  private queryProfileId: string;
  private fieldList: string[] = ['*'];
  private tags: string[] = [];

  constructor(queryProfileId?: string) {
    super();

    if (queryProfileId) {
      this.setQueryProfileId(queryProfileId);
    }

    this.searchDocs = debouncePromise(this.searchDocs, 200);
  }

  /**
   * == Public methods =============================================================================
   */

  /**
   * @override
   */

  load(): Promise<any> {
    return super.load()
      .then(() => this.getNotificationPayload());
  }

  getMeta() {
    const props = this.cloneProps(['searchParams', 'activeFacets', 'focusDocs']);

    const grouping = {
      mode: this.groupMode,
      field: this.groupBy,
      countProp: this.groupCountProp
    };

    _.merge(props, {
      searchParams: {
        numFound: this.searchParams.numFound,
        fqs: _.get(this.getLoadConfig(), 'urlParams.fq'),
      }
    });

    return {  ...super.getMeta(), grouping, ...props, queryProfileId: this.queryProfileId };
  }

  doSearchDocs(options?: any, defaultParams?: any) {
    const config = {
      name: 'query',
    };
    return this.callApiMethod(config, options, defaultParams);
  }

  async searchDocs(params: any) {
    const pathParams = { queryProfileId: this.getQueryProfileId() };
    const options = this.getLoadingParams(params);
    let response;

    try {
      response = await this.doSearchDocs(options, { pathParams }) || {};
    } catch (e) {
      console.error(e);
    }

    return this.getResultsMeta(response);
  }

  setTags(tags: string[]) {
    this.tags = [...tags];
    this.clear();
  }

  getActiveTags(): string[] {
    return !_.isEmpty(this.tags) ? this.tags : [];
  }

  clearFacets(): Promise<any> {
    return this.setFacets([]);
  }

  setQueryProfileId(queryProfileId: string) {

    this.queryProfileId = queryProfileId;

    this.setApiConfig({
      method: 'search',
      prop: 'pathParams',
      value: { queryProfileId: this.getQueryProfileId() },
    })
  }

  getQueryProfileId() {
    return this.queryProfileId || _.get(this.config.api, 'search.pathParams.id');
  }

  getGroupContent(...args) {
    const value = args[1];
    const meta = this.getMeta();
    const doc = _.find(meta.data.rawData.results.list.docs, { groupValue: value});
    return _.map(_.get(doc, 'groupedDocs.docs', [doc]), this.transformDoc);
  }

  /**
   * @override
   */

  transFormSearchResult(data) {
    const { numFound, docs, responseHeader, rawData } = this.getResultsMeta(data);
    const facets = data.facets || {};

    MoUtils.setInnumerableProp(this.searchParams, 'numFound', numFound);

    return {
      docs,
      rawData,
      responseHeader,

      // In accordance with legacy mode ranges duplicated at fields.
      facets: {
        fields: [..._.map(facets.field, this.transformFacetFields), ..._.map(facets.range, this.transformFacetRanges)],
        ranges: _.map(facets.range, this.transformFacetRanges),
      },
    };
  }

  /**
   * == Protected and private methods ==============================================================
   */

  protected override getLoadConfig() {
    const apiConfig: any = _.cloneDeep(this.apiConfig.search);
    apiConfig.urlParams = { ...apiConfig.urlParams };

    if (_.isEmpty(this.activeFacets)) {
      delete apiConfig.urlParams.fq;
    } else {
      apiConfig.urlParams.fq = _.map([..._.flatten([this.getFacetsFq(this.activeFacets)]), ...(apiConfig.urlParams.fq || [])], (fq: string) => encodeURIComponent(fq));
    }

    return apiConfig;
  }

  protected checkResultGrouped(data) {
    if (!_.isUndefined(_.get(data.results, 'groupField'))) {
      this.groupMode = 'collapse-expand';
      this.groupBy = _.get(data.results, 'groupField');
      return true;
    }
  }

  protected override getResultsMeta(data) {
    const { results, meta } = data;
    let docs: any;
    let responseHeader: any;
    let fieldList: any;
    let numFound: number;

    if (meta) {
      responseHeader = {
        params: meta.debug?.solrParams || {},
        totalTime: meta.timing.total,
        QTime: meta.timing.mainQuery,
      };

      fieldList = responseHeader?.params.fl;

      responseHeader.params.q = responseHeader.params.userQuery;
    }

    if (_.isString(fieldList)) {
      responseHeader.params.fl = fieldList.split(',').map(_.trim);
    }

    if (results && results.list) {
      const list = data.results.list;
      docs = this.checkResultGrouped(data)? _.map(list.docs, this.transformGroups) : _.map(list.docs, this.transformDoc);
      numFound = list.hits;
    }

    return {
      docs,
      numFound,
      responseHeader,
      rawData: data,
    };
  }
  protected override transformFacetFields = (meta, title) => {
    const facets = _.reject(_.map(meta.counts, ({ name, count }) => ({ title, count, value: name })), facet => !facet.count);

    return { title, facets };
  };

  protected transformFacetRanges = (meta, title: string) => {
    const other = _(meta).pick(['after', 'before', 'between']).omitBy((val) => val === 0).value();

    meta.start = _.head(meta.counts)['name'];
    meta.end = _.last(meta.counts)['name'];

    const facets = _(meta.counts)
      .map(({name, count}, index, array) => ({
        title,
        value: `${name} - ${array[index + 1] && array[index + 1]['name'] || meta.end}`,
        count: Number(count)
      }))
      .reject({ count: 0 })
      .value();

    return { title, facets, other };
  };

  protected getLoadingParams(params?: any) {
    return {
      pathParams: {
        queryProfileId: this.queryProfileId,
      },

      data: this.getRequestBody(params),
    }
  }

  protected transformDoc = (doc) => {
    return {
      id: doc.id,
      score: doc.score,
      ...doc.fields
    };
  };

  private transformGroups = (doc) => {
    const { hits } = !_.isUndefined(doc.groupedDocs)? doc.groupedDocs : { hits: 1 };

    return {
      id: doc.id,
      score: doc.score,
      ...doc.fields,
      [this.groupCountProp]: hits,
    };
  };

  private createFilterQuery(facet): SearchDSLQuery {
    let filterQuery;

    if (facet.type === 'range') {
      filterQuery = {
        type: facet.type,
        field: facet.fieldName,
        lower: _.head(facet.fieldValue.split(' ')),
        upper: _.last(facet.fieldValue.split(' ')),
      }
    } else {
      filterQuery = {
        type: 'singleTerm',
        field: facet.fieldName,
        value: facet.fieldValue
      }
    }

    return filterQuery;
  }

  private addFqToRequestBody(requestBody: SearchDSLRequest) {
    const queryDef = requestBody.queryDefinition;
    const namedFilters = [];
    const filters = [];

    _.each(_.groupBy(this.activeFacets, 'multi'), (fields, multi) => {
      _.each(_.groupBy(fields, 'fieldName'), (facets, fieldName) => {
        if (multi === 'true') {
          namedFilters.push({
            name: fieldName,
            query: {
              type: 'compound',
              should: [..._.map(facets, facet => this.createFilterQuery(facet))]
            }
          })
        } else {
          filters.push(..._.map(facets, facet => this.createFilterQuery(facet)));
        }
      })
    });

    if (namedFilters.length) {
      queryDef.namedFilters = namedFilters;
    }

    if (filters.length) {
      queryDef.filters = filters;
    }
  }

  /**
   * https://lucidworks.atlassian.net/wiki/spaces/ENGSTDS/pages/249102682/Request
   */
  private getRequestBody(params?: any): SearchDSLRequest {
    const { q: userQuery, start, rows: size, sortBy, sortAsc } = params?.queryParams || this.searchParams;

    const apiService: FusionApiService = getStoreApiService();
    const currentAppId = apiService.getAppId();

    const result = {
      queryDefinition: {
        userQuery,
      },

      results: {
        start,
        size,
        fields: this.fieldList,
        returnScore: true
      },
      context: {
        "lw.rules.debug": "1",
        "lw.tagger.debug": "1",
        ...(currentAppId && {'lw.rules.simulate': `${currentAppId}_query_rewrite_staging`})
      }
    };

    if (!_.isEmpty(this.tags)) {
      result.context['tags'] = this.tags;
    }

    if (sortBy) {
      _.set(result, 'results.sort', [{
        type: 'field',
        value: sortBy,
        order: sortAsc ? 'asc' : 'desc',
      }]);
    }

    if (this.activeFacets) {
      this.addFqToRequestBody(result);
    }


    return result;
  }
}
